import { z } from 'zod';
import { abilityNameSchema } from '../content/common.js';
import { abilityMethodsSchema } from '../content/rule.js';
import { equipmentItemSchema } from '../content/starting-equipment.js';
import { contentIdSchema } from '../rules/content-id.js';
/**
 * План создания персонажа: какие выборы предстоит сделать.
 *
 * План не хранится и не пишется руками — он вычисляется из записей
 * контента (`packages/rules`, creationPlan). Слот в плане означает
 * ровно одно: какая-то запись объявила выбор о себе сама. Поэтому
 * добавление класса в `content/srd/` меняет план, а кода не касается.
 */
/** Ключ слота: `species`, `class.skills`, `species.elven_lineage`. */
export const slotKeySchema = z.string().regex(/^[a-z0-9_]+(\.[a-z0-9_]+)?$/);
/**
 * Состав варианта, если вариант — не одна запись, а набор.
 *
 * Так устроено только стартовое снаряжение: «Chain Mail, Greatsword,
 * Flail и 4 GP» — это четыре разные вещи в одной строке. Раньше строка
 * склеивалась на сервере и приходила готовой английской фразой,
 * и перевести её было нечем: чтобы сказать «2 × Кинжал», нужен предмет
 * и количество, а не предложение.
 */
export const creationBundleSchema = z.strictObject({
    /** Та же форма, что в записи контента: второго описания предмета нет. */
    items: z.array(equipmentItemSchema),
    goldGp: z.number().nonnegative(),
});
/**
 * Владение, которое у персонажа уже есть (D63).
 *
 * `granted` — его дала запись сама: предыстория Послушника даёт
 * Проницательность, и взять её ещё раз в слоте класса — потратить
 * выбор впустую. `picked` — его выбрали в другом слоте: «Умелость»
 * человека и навыки класса предлагают одно и то же.
 *
 * Вариант при этом не убирается из списка, а помечается: список,
 * который меняется от выборов в соседних слотах, прыгает под рукой,
 * а уже отмеченный вариант пропадал бы с экрана, оставаясь выбранным.
 */
export const optionTakenSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('granted'), source: contentIdSchema }),
    z.strictObject({ kind: z.literal('picked'), slot: slotKeySchema }),
]);
export const creationOptionSchema = z.strictObject({
    /** Что попадёт в журнал: ссылка на контент либо литерал варианта. */
    value: z.string().min(1),
    /**
     * Подпись из источника, дословно и по-английски. Интерфейс переводит
     * её сам; здесь она остаётся запасным вариантом на случай, когда
     * перевода нет, и ответом на вопрос «что было в источнике».
     */
    label: z.string().min(1),
    /** Состав набора; `null` — вариант ссылается на одну запись. */
    bundle: creationBundleSchema.nullable(),
    /**
     * Описание варианта из записи контента; `null` — описания нет
     * (D57). Нужно затем, что по одному названию выбор не делается:
     * «Умелый» или «Бдительный» — это два слова, за которыми стоят
     * разные правила, и игрок видел только слова.
     *
     * Заполняется не у всех вариантов, и это осознанно: описания
     * заклинаний — 462 КБ против 32 КБ на все прочие виды записей,
     * а у заклинаний есть своя вкладка, где описание и показывается.
     */
    text: z.string().nullable().default(null),
    /** Наш перевод описания варианта, если он есть (D32). */
    textRu: z.string().nullable().default(null),
    /**
     * Это владение у персонажа уже есть, и откуда (D63). Нет поля —
     * вариант свободен. Необязательное, а не со значением по умолчанию:
     * вариантов в плане сотни, и метка нужна единицам.
     */
    taken: optionTakenSchema.optional(),
});
export const creationSlotSchema = z.strictObject({
    key: slotKeySchema,
    name: z.string().min(1),
    /** Запись, объявившая выбор: по ней видно, откуда взялся слот. */
    source: contentIdSchema,
    choose: z.number().int().positive(),
    options: z.array(creationOptionSchema).min(1),
    /**
     * Описание самого выбора — текст той особенности, которая его
     * объявила (D57). У вида это черта: «Разносторонность» — заголовок,
     * а то, что она значит, написано в `srd:human#versatile`.
     *
     * `null` — описания в записи нет вовсе. Так у слотов, за которыми
     * стоит строка таблицы класса, а не особенность: «Владение
     * навыками» объясняет себя названием, и выдумывать ему текст
     * вместо источника мы не станем.
     */
    text: z.string().nullable().default(null),
    /** Наш перевод описания выбора, если он есть (D32). */
    textRu: z.string().nullable().default(null),
    /**
     * Может ли выбор в этом слоте добавить в план новые слоты (D58).
     *
     * «Разносторонность» — может: выбранная черта объявляет свои
     * выборы. «Владение навыками» — нет: навык ни о чём не спрашивает.
     *
     * Поле нужно клиенту, а не движку: по нему он решает, стоит ли
     * перезапрашивать план. Без него пришлось бы запрашивать его
     * на каждую отметку — а в плане с выбранным классом лежат ещё
     * и заклинания.
     */
    expands: z.boolean().default(false),
});
/**
 * Прибавки характеристик от предыстории: какие характеристики
 * предлагает предыстория и какими раскладками их разрешает
 * увеличить правило создания.
 */
export const abilityIncreasePlanSchema = z.strictObject({
    abilities: z.array(abilityNameSchema).min(1),
    patterns: z.array(z.array(z.number().int().positive()).min(1)).min(1),
    maxScore: z.number().int().positive(),
});
export const creationPlanSchema = z.strictObject({
    /** Выборы «выбрать N из списка» — однородные, их показывает визард. */
    slots: z.array(creationSlotSchema),
    /**
     * Характеристики устроены иначе: это не выбор из списка, а числа
     * и способ их получения. Поэтому они отдельной частью плана,
     * а не слотом с вариантами.
     */
    abilities: z.strictObject({
        methods: abilityMethodsSchema,
        /** Появляется, когда предыстория выбрано: прибавки — от него. */
        increase: abilityIncreasePlanSchema.nullable(),
    }),
});
/**
 * План повышения уровня (D31).
 *
 * Что придёт само — особенности уровня; что предстоит выбрать —
 * теми же слотами, что и при создании: интерфейс не различает,
 * откуда слот пришёл.
 */
export const levelPlanSchema = z.strictObject({
    /** Уровень, на который поднимаемся. */
    level: z.number().int().min(2).max(20),
    hitDie: z.string().min(1),
    /** Фиксированные хиты за уровень: половина кости плюс один (с. 23). */
    fixedHp: z.number().int().positive(),
    features: z.array(z.strictObject({
        key: z.string().min(1),
        name: z.string().min(1),
        text: z.string(),
        /** Наш перевод описания, если он есть (D32). */
        textRu: z.string().nullable(),
    })),
    slots: z.array(creationSlotSchema),
    /**
     * Прибавка характеристик, если уровень её даёт; иначе `null`.
     * Раскладки — из записи правил, список характеристик — все шесть:
     * «one ability score of your choice» (с. 88).
     */
    increase: abilityIncreasePlanSchema.nullable(),
});
//# sourceMappingURL=plan.js.map